import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import WritingSettings, { GearIcon } from './WritingSettings';

const API = process.env.REACT_APP_BACKEND_URL;

// The writing room: the story as it is now, and a chat with the writer. Say
// (or type) what you want changed, or ask what it thinks. The writer decides
// what that needs — a line, a scene, the whole thing — makes the change, has
// the editor check it for slips and AI-isms, and tells you what it did.
// Every change is saved; "undo" goes back one step and "history" has the rest.

const Icon = ({ children, size = 16, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const MicIcon = (p) => <Icon {...p}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></Icon>;
const SendIcon = (p) => <Icon {...p}><path d="M12 19V5" /><path d="M6 11l6-6 6 6" /></Icon>;
const UndoIcon = (p) => <Icon {...p}><path d="M9 14L4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" /></Icon>;
const RedoIcon = (p) => <Icon {...p}><path d="M15 14l5-5-5-5" /><path d="M20 9H10a6 6 0 0 0 0 12h3" /></Icon>;
const CheckIcon = (p) => <Icon {...p}><path d="M5 12l5 5 9-10" /></Icon>;
const BookmarkIcon = (p) => <Icon {...p}><path d="M6 3h12v18l-6-4-6 4z" /></Icon>;
const ClockIcon = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>;
const CopyIcon = (p) => <Icon {...p}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></Icon>;
const TrashIcon = (p) => <Icon {...p}><path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M6 7l1 13h10l1-13" /><path d="M9 7V4h6v3" /></Icon>;
const BooksIcon = (p) => <Icon {...p}><path d="M4 19V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v14" /><path d="M10 19V7a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v12" /><path d="M3 19h18" /></Icon>;
const StopIcon = (p) => <Icon {...p}><rect x="7" y="7" width="10" height="10" rx="1.5" /></Icon>;

const paras = (t) => (t || '').split(/\n\s*\n/).map(x => x.trim()).filter(Boolean);
const num = (n) => Number(n || 0).toLocaleString();
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? clock(iso) : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${clock(iso)}`;
};

// compare the version before the last change with now. paragraphs are lined
// up first; where one was reworded, the changed words are marked inside it.
function lcsPairs(a, b) {
  const L = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) for (let j = b.length - 1; j >= 0; j--) L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = []; let i = 0; let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { out.push({ t: 'same', v: a[i] }); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) out.push({ t: 'out', v: a[i++] });
    else out.push({ t: 'in', v: b[j++] });
  }
  while (i < a.length) out.push({ t: 'out', v: a[i++] });
  while (j < b.length) out.push({ t: 'in', v: b[j++] });
  return out;
}
function wordDiff(before, after) {
  const a = before.split(/\s+/); const b = after.split(/\s+/);
  if (a.length * b.length > 400000) return [{ t: 'out', v: before }, { t: 'in', v: after }];
  // merge runs so the marks read as phrases, not single words
  const merged = [];
  lcsPairs(a, b).forEach(x => {
    const last = merged[merged.length - 1];
    if (last && last.t === x.t) last.v += ` ${x.v}`; else merged.push({ ...x });
  });
  return merged;
}
function diffParas(before, after) {
  const rows = lcsPairs(paras(before), paras(after));
  const out = [];
  for (let k = 0; k < rows.length; k++) {
    if (rows[k].t === 'same') { out.push({ t: 'same', p: rows[k].v }); continue; }
    const outs = []; const ins = [];
    while (k < rows.length && rows[k].t !== 'same') { (rows[k].t === 'out' ? outs : ins).push(rows[k].v); k++; }
    k--;
    const n = Math.max(outs.length, ins.length);
    for (let m = 0; m < n; m++) {
      if (outs[m] !== undefined && ins[m] !== undefined) out.push({ t: 'edit', parts: wordDiff(outs[m], ins[m]) });
      else if (outs[m] !== undefined) out.push({ t: 'out', p: outs[m] });
      else out.push({ t: 'in', p: ins[m] });
    }
  }
  return out;
}

const SpeechRec = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const WHO = { writer: 'writer', editor: 'editor', you: 'you' };

export default function WritingRoom() {
  const navigate = useNavigate();
  const { pieceId } = useParams();
  const token = localStorage.getItem('token');

  const [piece, setPiece] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [wide, setWide] = useState(typeof window !== 'undefined' ? window.innerWidth >= 900 : true);
  const [tab, setTab] = useState('story');
  const [seenCount, setSeenCount] = useState(0);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [openVersion, setOpenVersion] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState('');

  const busyRef = useRef(false);
  const recRef = useRef(null);
  const chatEnd = useRef(null);
  const inputRef = useRef(null);
  const lastVersion = useRef(null);

  const load = () => {
    axios.get(`${API}/writing/pieces/${pieceId}?token=${token}`)
      .then(res => {
        const p = res.data.piece;
        setPiece(p);
        busyRef.current = p.busy;
        setErrorMsg('');
        if (lastVersion.current !== null && p.version !== lastVersion.current) setShowDiff(false);
        lastVersion.current = p.version;
      })
      .catch(err => setErrorMsg(err?.response?.data?.detail || "couldn't open this"));
  };

  useEffect(() => {
    if (!token) { navigate('/'); return undefined; }
    setPiece(null); lastVersion.current = null; setSeenCount(0);
    load();
    // quick while it's working, slow otherwise
    let n = 0;
    const t = setInterval(() => { n += 1; if (busyRef.current || n % 4 === 0) load(); }, 2000);
    const onResize = () => setWide(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => { clearInterval(t); window.removeEventListener('resize', onResize); if (recRef.current) recRef.current.abort(); };
  }, [pieceId]);

  const msgCount = piece ? piece.messages.length : 0;
  const chatVisible = wide || tab === 'chat';
  useEffect(() => { if (chatVisible) setSeenCount(msgCount); }, [chatVisible, msgCount]);
  useEffect(() => { if (chatVisible && chatEnd.current) chatEnd.current.scrollIntoView({ block: 'end' }); }, [msgCount, chatVisible, piece && piece.busy]);

  // the box grows with what you say, up to a few lines
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text, wide, tab]);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2200); };

  const send = (raw) => {
    const say = (raw ?? text).trim();
    if (!say || sending || (piece && piece.busy)) return;
    if (listening && recRef.current) recRef.current.stop();
    setSending(true);
    axios.post(`${API}/writing/pieces/${pieceId}/say`, { token, text: say })
      .then(res => { setPiece(res.data.piece); busyRef.current = res.data.piece.busy; if (raw === undefined) setText(''); })
      .catch(err => setErrorMsg(err?.response?.data?.detail || "that didn't send — try again"))
      .finally(() => setSending(false));
  };

  const toggleMic = () => {
    if (!SpeechRec) return;
    if (listening) { recRef.current && recRef.current.stop(); return; }
    const rec = new SpeechRec();
    rec.continuous = true; rec.interimResults = true; rec.lang = navigator.language || 'en-US';
    const base = text.trim();
    rec.onresult = (e) => {
      let heard = '';
      for (let i = 0; i < e.results.length; i++) heard += e.results[i][0].transcript;
      setText((base ? base + ' ' : '') + heard.trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch (e) { setListening(false); }
  };

  const post = (path, body, after) => axios.post(`${API}/writing/pieces/${pieceId}/${path}`, { token, ...body })
    .then(res => { setPiece(res.data.piece); busyRef.current = res.data.piece.busy; lastVersion.current = res.data.piece.version; after && after(); })
    .catch(err => setErrorMsg(err?.response?.data?.detail || "that didn't work — try again"));

  const openHistory = () => {
    setMenuOpen(false); setHistory([]); setOpenVersion(null);  // the history sheet replaces the settings sheet
    axios.get(`${API}/writing/pieces/${pieceId}/history?token=${token}`)
      .then(res => setHistory(res.data.versions || []))
      .catch(() => { setHistory(null); setErrorMsg("couldn't load the history"); });
  };

  const copyStory = () => {
    setMenuOpen(false);
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(piece.content).then(() => showToast('copied'), () => showToast("couldn't copy"));
  };

  if (!piece) {
    return (
      <div style={s.page}>
        <header style={s.header}><button style={s.back} onClick={() => navigate('/writing')} aria-label="back">←</button></header>
        <div style={{ padding: '30px 20px', color: '#8a7466', fontSize: '13px' }}>{errorMsg || 'opening…'}</div>
      </div>
    );
  }

  const series = piece.kind === 'series';
  const lc = piece.last_change;
  const act = piece.activity;
  const unread = !chatVisible && msgCount > seenCount;
  const backTo = series ? `/writing/book/${piece.work_id}` : '/writing';
  const chTitle = /^Chapter \d+$/.test(piece.title) ? '' : piece.title;

  // ---------- the story ----------
  const story = (
    <div style={s.storyInner}>
      {piece.busy && act && (
        <div style={s.workingPill} role="status">
          <span className="wr-pulse" style={s.liveDot} />
          <span style={{ flex: 1 }}>{act.label}…</span>
          <span style={s.pillBar}><span style={{ ...s.pillFill, width: `${Math.round(act.progress * 100)}%` }} /></span>
        </div>
      )}

      {!piece.busy && lc && (
        <div style={s.changeBar}>
          <div style={s.changeText}>
            <span style={s.changeWho}>{lc.by === 'you' ? 'you' : `the ${lc.by}`}</span>{' '}
            {lc.by === 'you' ? lc.summary : lc.summary}
          </div>
          <div style={s.changeBtns}>
            {lc.previous_content && (
              <button className="wr-btn" style={s.pillBtn(showDiff)} onClick={() => setShowDiff(v => !v)} aria-pressed={showDiff}>
                {showDiff ? 'hide changes' : 'show changes'}
              </button>
            )}
            <button className="wr-btn" style={s.pillBtn(false)} onClick={() => post('undo', {}, () => showToast(lc.went_back ? 'put it back' : 'went back one step'))}>
              {lc.went_back ? <RedoIcon size={14} /> : <UndoIcon size={14} />}{lc.went_back ? 'redo' : 'undo'}
            </button>
          </div>
        </div>
      )}

      <div style={s.storyHead}>
        {series && <div style={s.chapterLabel}>chapter {piece.n}</div>}
        <h1 style={s.storyTitle}>{series ? (chTitle || `Chapter ${piece.n}`) : piece.title}</h1>
        <div style={s.storyMeta}>{num(piece.words)} words</div>
      </div>

      {!piece.busy && !piece.versions_count && (
        <div style={s.retryCard}>
          <div>{piece.status === 'queued' ? 'this one is queued — it starts when the chapter before it is done.' : "this hasn't been written yet (or the last try didn't finish)."}</div>
          {piece.status !== 'queued' && (
            <button className="wr-btn" style={s.bringBack} onClick={() => post('draft', {}, () => showToast('writing it now'))}>write it now</button>
          )}
        </div>
      )}

      {!piece.content && piece.busy && <div style={s.planning}>planning the scenes from your brief{series ? ', the bible and the chapters before this one' : ''}…</div>}

      <div style={s.prose}>
        {showDiff && lc && lc.previous_content
          ? diffParas(lc.previous_content, piece.content).map((d, i) => (
            d.t === 'edit'
              ? <p key={i} style={s.p}>{d.parts.map((w, k) => (
                <React.Fragment key={k}>{k > 0 ? ' ' : ''}<span style={w.t === 'out' ? s.wOut : w.t === 'in' ? s.wIn : undefined}>{w.v}</span></React.Fragment>
              ))}</p>
              : <p key={i} style={d.t === 'out' ? s.pOut : d.t === 'in' ? s.pIn : s.p}>{d.p}</p>
          ))
          : paras(piece.content).map((p, i, arr) => (
            <p key={i} style={s.p}>{p}{piece.busy && act && act.live && i === arr.length - 1 && <span className="wr-caret" style={s.caret} />}</p>
          ))}
      </div>

      {series && (piece.prev_id || piece.next_id) && (
        <div style={s.chapterNav}>
          {piece.prev_id ? <button className="wr-btn" style={s.navBtn} onClick={() => navigate(`/writing/p/${piece.prev_id}`)}>← chapter {piece.n - 1}</button> : <span />}
          {piece.next_id ? <button className="wr-btn" style={s.navBtn} onClick={() => navigate(`/writing/p/${piece.next_id}`)}>chapter {piece.n + 1} →</button> : <span />}
        </div>
      )}
    </div>
  );

  // ---------- the chat ----------
  const chat = (
    <div style={s.chatInner}>
      {piece.messages.length === 0 && !piece.busy && (
        <div style={s.chatEmpty}>
          talk to the writer like you would a co-writer. try “make the ending hit harder”, “she'd never say that”,
          or “what do you think of the pacing?”
        </div>
      )}
      {piece.messages.map(m => (
        <div key={m.id} style={s.msgWrap(m.role === 'you')}>
          {m.role !== 'you' && (
            <div style={s.msgWho(m.role)}>
              {WHO[m.role]}{m.model && <><span style={s.whoBar} aria-hidden="true" /><span style={s.whoModel}>{m.model}</span></>}
            </div>
          )}
          <div style={s.bubble(m.role)}>
            {paras(m.text).map((t, i) => <p key={i} style={s.msgP}>{t}</p>)}
            {m.changes && (
              <button className="wr-btn" style={s.changeLink}
                onClick={() => { if (!wide) setTab('story'); if (lc && lc.version === m.changes.version) setShowDiff(true); }}>
                <CheckIcon size={13} /> {m.changes.summary}{lc && lc.version === m.changes.version ? ' · see it' : ''}
              </button>
            )}
            {m.remembered && (
              <div style={s.remembered}><BookmarkIcon size={13} /> saved to the story bible: “{m.remembered}”</div>
            )}
            {m.suggestions && m.suggestions.length > 0 && (
              <div style={s.suggest}>
                {m.suggestions.map(sg => (
                  <button key={sg} className="wr-btn" style={s.suggestBtn} disabled={piece.busy || sending} onClick={() => send(sg)}>{sg}</button>
                ))}
              </div>
            )}
          </div>
          <div style={s.msgTime(m.role === 'you')}>{when(m.at)}</div>
        </div>
      ))}

      {piece.busy && act && (
        <div style={s.msgWrap(false)}>
          <div style={s.msgWho('writer')}>working</div>
          <div style={s.workBubble}>
            {act.steps.map((st, i) => (
              <div key={i} style={s.step}>
                <span style={s.stepMark(st.done)}>{st.done ? <CheckIcon size={12} /> : <span className="wr-pulse" style={s.stepDot} />}</span>
                <span style={s.stepWho}>{st.who}</span>
                <span style={{ color: st.done ? '#9a8676' : '#e0d4c6' }}>{st.text}</span>
              </div>
            ))}
            <button className="wr-btn" style={s.stopBtn} onClick={() => post('stop', {})}><StopIcon size={12} /> stop</button>
          </div>
        </div>
      )}
      <div ref={chatEnd} />
    </div>
  );

  const composer = (
    <div style={s.composer(wide)}>
      <div style={s.inputWrap(listening)}>
        <textarea
          ref={inputRef}
          className="wr-in"
          rows={1}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && wide) { e.preventDefault(); send(); } }}
          placeholder={listening ? 'listening… just talk' : piece.busy ? "it's working…" : 'talk to the writer…'}
          style={s.input}
          aria-label="message to the writer"
        />
        {SpeechRec && (
          <button className="wr-btn" style={s.micBtn(listening)} onClick={toggleMic} aria-label={listening ? 'stop listening' : 'talk'} aria-pressed={listening}>
            <MicIcon size={18} />
          </button>
        )}
        <button className="wr-btn" style={text.trim() && !piece.busy && !sending ? s.sendBtn : s.sendOff}
          disabled={!text.trim() || piece.busy || sending} onClick={() => send()} aria-label="send">
          <SendIcon size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .wr-btn:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        .wr-in:focus { outline: none; }
        .wr-in::placeholder { color: #6d5d4e; }
        .wr-pulse { animation: wrpulse 1.4s ease-in-out infinite; }
        @keyframes wrpulse { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
        .wr-caret { animation: wrcaret 1s steps(2) infinite; }
        @keyframes wrcaret { 50% { opacity: 0; } }
        .wr-mic-on { animation: wrmic 1.2s ease-in-out infinite; }
        @keyframes wrmic { 0%,100% { box-shadow: 0 0 0 0 rgba(201,147,90,0.45); } 50% { box-shadow: 0 0 0 6px rgba(201,147,90,0); } }
        @media (prefers-reduced-motion: reduce) { .wr-pulse, .wr-caret, .wr-mic-on { animation: none; } }
      `}</style>

      <header style={s.header}>
        <button className="wr-btn" style={s.back} onClick={() => navigate(backTo)} aria-label="back">←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.title}>{series ? piece.work_title : piece.title}</div>
          <div style={s.sub}>{series ? `chapter ${piece.n} of ${piece.chapters_planned}` : 'one-shot'}</div>
        </div>
        <button className="wr-btn" style={s.dotsBtn} onClick={() => setMenuOpen(true)} aria-label="settings" title="settings"><GearIcon /></button>
      </header>

      {!wide && (
        <div style={s.tabs} role="tablist">
          {['story', 'chat'].map(k => (
            <button key={k} role="tab" aria-selected={tab === k} className="wr-btn" style={s.tab(tab === k)} onClick={() => setTab(k)}>
              {k}{k === 'chat' && unread && <span style={s.unread} aria-label="new messages" />}
              {k === 'chat' && piece.busy && tab !== 'chat' && <span className="wr-pulse" style={{ ...s.unread, background: '#c9935a' }} />}
            </button>
          ))}
        </div>
      )}

      {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')} role="alert">{errorMsg}</div>}

      {wide ? (
        <div style={s.split}>
          <div style={s.storyCol}>{story}</div>
          <div style={s.chatCol}>
            <div style={s.chatHead}>talk to the writer</div>
            <div style={s.chatScroll}>{chat}</div>
            {composer}
          </div>
        </div>
      ) : (
        <>
          <div style={s.single}>{tab === 'story' ? story : chat}</div>
          {composer}
        </>
      )}

      {menuOpen && (
        <WritingSettings
          work={{ id: piece.work_id, kind: piece.kind, writer_model: piece.writer_model, editor_model: piece.editor_model, bible_count: piece.bible_count, suggestions: piece.suggestions, cost_usd: piece.cost_usd }}
          onClose={() => setMenuOpen(false)} onSaved={load}
          onOpenBible={() => navigate(`/writing/bible/${piece.work_id}`)}>
          <button className="wr-btn" style={s.sheetRow} onClick={openHistory} disabled={!piece.versions_count}>
            <ClockIcon size={16} /><span style={{ flex: 1, textAlign: 'left' }}>history<span style={s.sheetRowSub}>{piece.versions_count} versions of this {series ? 'chapter' : 'story'}</span></span>
          </button>
          <button className="wr-btn" style={s.sheetRow} onClick={copyStory} disabled={!piece.content}>
            <CopyIcon size={16} /><span style={{ flex: 1, textAlign: 'left' }}>copy the {series ? 'chapter' : 'story'}</span>
          </button>
          {series && (
            <button className="wr-btn" style={s.sheetRow} onClick={() => navigate(backTo)}>
              <BooksIcon size={16} /><span style={{ flex: 1, textAlign: 'left' }}>all chapters</span>
            </button>
          )}
          {!series && (
            <button className="wr-btn" style={{ ...s.sheetRow, color: '#c98a7a' }} onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}>
              <TrashIcon size={16} /><span style={{ flex: 1, textAlign: 'left' }}>delete this story</span>
            </button>
          )}
        </WritingSettings>
      )}

      {history && (
        <div style={s.overlay} onClick={() => setHistory(null)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label="history">
            <div style={s.sheetHead}>
              <div style={s.sheetTitle}>history</div>
              <button className="wr-btn" style={s.dotsBtn} onClick={() => setHistory(null)} aria-label="close">×</button>
            </div>
            <div style={s.hint}>every change is kept. open one to read it, or bring it back — that becomes the newest version, so nothing is lost.</div>
            <div style={s.sheetList}>
              {history.length === 0 && <div style={s.hint}>loading…</div>}
              {history.map(v => (
                <div key={v.n} style={s.hItem(v.n === piece.version)}>
                  <button className="wr-btn" style={s.hRow} onClick={() => setOpenVersion(openVersion === v.n ? null : v.n)} aria-expanded={openVersion === v.n}>
                    <span style={s.hNum}>{v.n}</span>
                    <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <span style={s.hSum}>{v.summary}</span>
                      <span style={s.hMeta}>{v.by} · {num(v.words)} words · {when(v.at)}{v.n === piece.version ? ' · on screen now' : ''}</span>
                    </span>
                  </button>
                  {openVersion === v.n && (
                    <div style={s.hOpen}>
                      <div style={s.hProse}>{paras(v.content).map((p, i) => <p key={i} style={s.hP}>{p}</p>)}</div>
                      {v.n !== piece.version && (
                        <button className="wr-btn" style={s.bringBack} disabled={piece.busy}
                          onClick={() => post('restore', { version: v.n }, () => { setHistory(null); showToast(`brought back version ${v.n}`); })}>
                          bring this one back
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={s.overlay} onClick={() => setConfirmDelete(false)}>
          <div style={{ ...s.sheet, maxWidth: '380px' }} onClick={e => e.stopPropagation()} role="dialog" aria-label="delete">
            <div style={s.sheetTitle}>delete “{piece.title}”?</div>
            <div style={s.hint}>the story, its history and the chat all go. this can't be undone.</div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button className="wr-btn" style={s.danger}
                onClick={() => axios.delete(`${API}/writing/pieces/${pieceId}?token=${token}`).then(() => navigate('/writing')).catch(() => setErrorMsg("couldn't delete it"))}>delete</button>
              <button className="wr-btn" style={s.quiet} onClick={() => setConfirmDelete(false)}>keep it</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={s.toast} role="status">{toast}</div>}
    </div>
  );
}

const serif = "'Cormorant Garamond', serif";
const sans = "'DM Sans', sans-serif";
const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', background: '#0e0b0f', color: '#e8ddd0', fontFamily: sans, fontWeight: 300 },
  header: { flexShrink: 0, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)', zIndex: 20 },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { fontFamily: serif, fontSize: '20px', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sub: { fontSize: '11.5px', color: '#8a7466', marginTop: '1px' },
  dotsBtn: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  tabs: { flexShrink: 0, display: 'flex', gap: '6px', padding: '10px 18px 8px', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  tab: (on) => ({ position: 'relative', flex: 1, padding: '9px 0', borderRadius: '14px', border: on ? 'none' : '1px solid rgba(201,147,90,0.15)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a', fontSize: '13.5px', fontFamily: sans, cursor: 'pointer', fontWeight: on ? 500 : 400 }),
  unread: { position: 'absolute', top: '8px', marginLeft: '6px', width: '7px', height: '7px', borderRadius: '50%', background: '#e8b27a' },
  split: { flex: 1, minHeight: 0, display: 'flex' },
  storyCol: { flex: 1, minWidth: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  chatCol: { width: '400px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid rgba(201,147,90,0.08)', background: '#120f16' },
  chatHead: { padding: '14px 18px 6px', fontSize: '10px', color: '#8a7466', letterSpacing: '0.14em', textTransform: 'uppercase' },
  chatScroll: { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  single: { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  storyInner: { maxWidth: '680px', margin: '0 auto', padding: '18px 22px 60px', boxSizing: 'border-box' },
  workingPill: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '14px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)', fontSize: '12.5px', color: '#c9a57a', marginBottom: '14px' },
  liveDot: { width: '8px', height: '8px', borderRadius: '50%', background: '#c9935a', flexShrink: 0 },
  pillBar: { width: '70px', height: '4px', borderRadius: '2px', background: 'rgba(201,147,90,0.15)', overflow: 'hidden', flexShrink: 0 },
  pillFill: { display: 'block', height: '100%', background: '#c9935a', transition: 'width .6s ease' },
  changeBar: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 12px', padding: '10px 12px 10px 14px', borderRadius: '14px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.1)', marginBottom: '16px' },
  changeText: { flex: '1 1 180px', fontSize: '12.5px', color: '#b8a594', lineHeight: 1.5 },
  changeWho: { color: '#c9935a' },
  changeBtns: { display: 'flex', gap: '6px', flexShrink: 0 },
  pillBtn: (on) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '14px', border: on ? 'none' : '1px solid rgba(201,147,90,0.22)', background: on ? 'rgba(201,147,90,0.18)' : 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer', fontFamily: sans }),
  storyHead: { margin: '10px 0 22px', textAlign: 'center' },
  chapterLabel: { fontSize: '10.5px', color: '#8a7466', letterSpacing: '0.18em', textTransform: 'uppercase' },
  storyTitle: { fontFamily: serif, fontWeight: 400, fontStyle: 'italic', fontSize: '32px', lineHeight: 1.15, margin: '6px 0 0', color: '#e8ddd0' },
  storyMeta: { fontSize: '11.5px', color: '#6d5d4e', marginTop: '8px' },
  retryCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center', padding: '22px 18px', borderRadius: '16px', border: '1px dashed rgba(201,147,90,0.25)', color: '#a8927f', fontSize: '13px', lineHeight: 1.6, marginBottom: '16px' },
  planning: { fontFamily: serif, fontStyle: 'italic', fontSize: '18px', color: '#8a7466', textAlign: 'center', padding: '30px 0' },
  prose: { fontFamily: serif, fontSize: '20px', lineHeight: 1.7, color: '#e8ddd0' },
  p: { margin: '0 0 1.05em' },
  pOut: { margin: '0 0 1.05em', color: '#a06a5e', textDecoration: 'line-through', textDecorationColor: 'rgba(200,110,95,0.6)', opacity: 0.8 },
  pIn: { margin: '0 0 1.05em', background: 'rgba(201,147,90,0.12)', borderLeft: '2px solid #c9935a', padding: '4px 0 4px 12px', marginLeft: '-14px', borderRadius: '0 6px 6px 0' },
  wOut: { color: '#b07565', textDecoration: 'line-through', textDecorationColor: 'rgba(200,110,95,0.7)' },
  wIn: { background: 'rgba(201,147,90,0.2)', color: '#f0dcc4', borderRadius: '3px', padding: '0 2px' },
  caret: { display: 'inline-block', width: '2px', height: '1em', background: '#c9935a', marginLeft: '3px', verticalAlign: '-2px' },
  chapterNav: { display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '30px', paddingTop: '18px', borderTop: '1px solid rgba(201,147,90,0.08)' },
  navBtn: { padding: '9px 16px', borderRadius: '14px', border: '1px solid rgba(201,147,90,0.18)', background: 'transparent', color: '#c9a57a', fontSize: '13px', cursor: 'pointer', fontFamily: sans },
  chatInner: { padding: '14px 16px 24px', display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '680px', margin: '0 auto', boxSizing: 'border-box', width: '100%' },
  chatEmpty: { fontSize: '13px', color: '#8a7466', lineHeight: 1.7, padding: '18px 16px', borderRadius: '16px', border: '1px dashed rgba(201,147,90,0.2)' },
  msgWrap: (me) => ({ display: 'flex', flexDirection: 'column', alignItems: me ? 'flex-end' : 'flex-start', gap: '4px' }),
  whoBar: { display: 'inline-block', width: '1px', height: '10px', background: 'rgba(201,147,90,0.35)', margin: '0 8px', verticalAlign: '-1px' },
  whoModel: { textTransform: 'none', letterSpacing: '0.02em', color: '#8a7466', fontSize: '11px' },
  sheetRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', borderRadius: '16px', border: 'none', background: '#17131b', color: '#e0d4c6', fontSize: '14px', cursor: 'pointer', fontFamily: sans },
  sheetRowSub: { display: 'block', fontSize: '11.5px', color: '#7a6a5a', marginTop: '2px' },
  msgWho: (role) => ({ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: role === 'editor' ? '#a08a9e' : '#c9935a', paddingLeft: '4px' }),
  bubble: (role) => ({
    maxWidth: '88%', padding: '11px 14px', borderRadius: role === 'you' ? '18px 18px 6px 18px' : '18px 18px 18px 6px', fontSize: '14px', lineHeight: 1.55,
    background: role === 'you' ? 'linear-gradient(135deg, #8a5f35, #b88550)' : role === 'editor' ? '#1b1722' : '#1e1825',
    color: role === 'you' ? '#1a120c' : '#e0d4c6', border: role === 'you' ? 'none' : `1px solid ${role === 'editor' ? 'rgba(160,138,158,0.18)' : 'rgba(201,147,90,0.12)'}`,
  }),
  msgP: { margin: '0 0 0.6em' },
  msgTime: (me) => ({ fontSize: '10.5px', color: '#5d4f44', padding: me ? '0 4px 0 0' : '0 0 0 4px' }),
  changeLink: { display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '2px', padding: '6px 10px', borderRadius: '10px', border: 'none', background: 'rgba(201,147,90,0.1)', color: '#c9935a', fontSize: '12px', cursor: 'pointer', fontFamily: sans, textAlign: 'left' },
  remembered: { display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px', fontSize: '12px', color: '#a8927f', lineHeight: 1.5 },
  suggest: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' },
  suggestBtn: { padding: '7px 12px', borderRadius: '14px', border: '1px solid rgba(201,147,90,0.28)', background: 'transparent', color: '#c9a57a', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans, textAlign: 'left' },
  workBubble: { maxWidth: '88%', padding: '12px 14px', borderRadius: '18px 18px 18px 6px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.2)', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' },
  step: { display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: 1.45 },
  stepMark: (done) => ({ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: done ? '#7fa37a' : '#c9935a', flexShrink: 0, marginTop: '1px' }),
  stepDot: { width: '7px', height: '7px', borderRadius: '50%', background: '#c9935a' },
  stepWho: { width: '44px', flexShrink: 0, fontSize: '11px', color: '#8a7466', paddingTop: '1px' },
  stopBtn: { alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '2px', padding: '5px 10px', borderRadius: '10px', border: '1px solid rgba(201,147,90,0.2)', background: 'transparent', color: '#9a8676', fontSize: '11.5px', cursor: 'pointer', fontFamily: sans },
  composer: (wide) => ({ flexShrink: 0, padding: wide ? '10px 14px 14px' : '10px 14px calc(14px + env(safe-area-inset-bottom))', borderTop: '1px solid rgba(201,147,90,0.08)', background: wide ? '#120f16' : '#0e0b0f' }),
  inputWrap: (on) => ({ display: 'flex', alignItems: 'flex-end', gap: '6px', padding: '6px 6px 6px 14px', borderRadius: '22px', background: '#1e1825', border: `1px solid ${on ? 'rgba(201,147,90,0.6)' : 'rgba(201,147,90,0.16)'}`, maxWidth: '680px', margin: '0 auto' }),
  input: { flex: 1, minWidth: 0, boxSizing: 'border-box', resize: 'none', border: 'none', background: 'transparent', color: '#e8ddd0', fontSize: '15px', lineHeight: 1.45, fontFamily: sans, padding: '8px 0', maxHeight: '140px', overflowY: 'auto', outline: 'none' },
  micBtn: (on) => ({ width: '38px', height: '38px', borderRadius: '50%', border: on ? 'none' : '1px solid rgba(201,147,90,0.22)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9935a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }),
  sendBtn: { width: '38px', height: '38px', borderRadius: '50%', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sendOff: { width: '38px', height: '38px', borderRadius: '50%', border: 'none', background: 'rgba(201,147,90,0.12)', color: 'rgba(201,147,90,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  overlay: { position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(8,6,10,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0' },
  sheet: { width: '100%', maxWidth: '560px', maxHeight: '86vh', display: 'flex', flexDirection: 'column', gap: '10px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '22px 22px 0 0', padding: '18px 18px 24px', boxSizing: 'border-box', boxShadow: '0 -10px 40px rgba(0,0,0,0.5)' },
  sheetHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  sheetTitle: { fontFamily: serif, fontSize: '24px', fontStyle: 'italic' },
  hint: { fontSize: '12px', color: '#8a7466', lineHeight: 1.6 },
  sheetList: { overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' },
  hItem: (cur) => ({ borderRadius: '14px', background: cur ? 'rgba(201,147,90,0.08)' : '#17131b', border: `1px solid ${cur ? 'rgba(201,147,90,0.3)' : 'transparent'}` }),
  hRow: { width: '100%', display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '11px 12px', border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontFamily: sans },
  hNum: { width: '24px', fontFamily: serif, fontSize: '20px', color: '#c9935a', lineHeight: 1, flexShrink: 0, paddingTop: '1px' },
  hSum: { display: 'block', fontSize: '13.5px', color: '#e0d4c6', lineHeight: 1.4 },
  hMeta: { display: 'block', fontSize: '11px', color: '#7a6a5a', marginTop: '3px' },
  hOpen: { padding: '0 12px 12px 48px' },
  hProse: { maxHeight: '240px', overflowY: 'auto', fontFamily: serif, fontSize: '16px', lineHeight: 1.6, color: '#c9b8a6', padding: '10px 12px', borderRadius: '10px', background: '#0e0b0f' },
  hP: { margin: '0 0 0.8em' },
  bringBack: { marginTop: '10px', padding: '9px 16px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '13px', cursor: 'pointer', fontFamily: sans },
  danger: { padding: '9px 18px', borderRadius: '12px', border: 'none', background: '#a0473a', color: '#fff', fontSize: '13px', cursor: 'pointer', fontFamily: sans },
  quiet: { padding: '9px 18px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.2)', background: 'transparent', color: '#c9a57a', fontSize: '13px', cursor: 'pointer', fontFamily: sans },
  error: { margin: '10px 18px 0', background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer' },
  toast: { position: 'fixed', left: '50%', bottom: '96px', transform: 'translateX(-50%)', zIndex: 60, background: '#2a2130', border: '1px solid rgba(201,147,90,0.3)', color: '#e8ddd0', fontSize: '13px', padding: '9px 16px', borderRadius: '14px' },
};
